import { FC } from "react";
import RegistrationForm from "./RegistrationForm";
import { Button, Card, CardHeader, CardPreview, Text } from "@fluentui/react-components";
import { useSearchParams } from "react-router";
import { ConferenceAttendee, IConference, IConferenceAttendee } from "./conferenceTypes";
import { useGeneralStyles } from "../styles";
import { useQuery } from "@tanstack/react-query";
import { nodeServer } from "../constants";
import { accessTokenInfo$ } from "../oauth/oauth";

interface IConfirmationProps {}

const Confirmation: FC<IConfirmationProps> = () => {
    const [searchParams] = useSearchParams();

    const { data: conference } = useQuery({
        queryKey: ["conferenceConfirmation"],
        refetchOnWindowFocus: false,
        enabled: true,
        queryFn: async (signal) => {
            const url = nodeServer + `conference/latest`;
            const res = await fetch(url, { signal: signal.signal });
            if (!res.ok) {
                throw new Error("Network response was not ok");
            }
            const conference: IConference = await res.json();
            console.log(conference);
            return conference;
        },
    });

    const { data: conferenceAttendee } = useQuery({
        queryKey: ["conferenceAttendeeConfirmation"],
        refetchOnWindowFocus: false,
        enabled: !!conference,
        queryFn: async (signal) => {
            const confirmationCode = searchParams.get("confirmationCode");
            if (confirmationCode !== null) {
                const url = nodeServer + `conference/confirmationCode/${confirmationCode}`;
                const res = await fetch(url, { method: "get", signal: signal.signal });
                if (!res.ok) {
                    throw new Error("Network response was not ok");
                }
                const json = await res.json();
                json.user.details = JSON.parse(json.user.details);
                console.log(json.user);
                return json.user as IConferenceAttendee;
            } else {
                if (accessTokenInfo$.value != null && accessTokenInfo$.value.accessToken != null) {
                    const headers = new Headers();
                    headers.append("Authorization", "Bearer " + accessTokenInfo$.value.accessToken);

                    // if (userState !== null) {
                    const url = nodeServer + `conference/status`;
                    const res = await fetch(url, { headers: headers, method: "get", signal: signal.signal });
                    if (!res.ok) {
                        throw new Error("Network response was not ok");
                    }
                    const json = await res.json();
                    console.log(json);
                    if (json.error) {
                        console.log(json.error);
                    }
                    if (json.user !== null) {
                        json.user.details = JSON.parse(json.user.details);
                        return json.user;
                    } else {
                        let attendee: IConferenceAttendee = new ConferenceAttendee();
                        ConferenceAttendee.setupDetails(attendee, conference!.data);
                        return attendee;
                    }
                } else {
                    let attendee: IConferenceAttendee = new ConferenceAttendee();
                    ConferenceAttendee.setupDetails(attendee, conference!.data);
                    for (const key of searchParams.keys()) {
                        if (key === "details") {
                            const details = searchParams.get(key);
                            attendee.details = JSON.parse(details || "{}");
                        } else {
                            attendee[key] = searchParams.get(key);
                        }
                    }
                    return attendee;
                }
            }
            //}
        },
    });

    const generalStyles = useGeneralStyles();

    return (
        <div>
            <div style={{ display: "flex", flexDirection: "column", flexWrap: "wrap" }}>
                <h2>Conference Registration Confirmation</h2>
                {conferenceAttendee !== undefined && (
                    <div>
                        {conferenceAttendee.payby === "check" && (
                            <>
                                <Card>
                                    <CardHeader header={<h3>Payment by Check</h3>} />
                                    <CardPreview style={{ margin: 0, marginTop: -10 }}>
                                        <Text className={generalStyles.body2}>
                                            Make checks payable to 2YC3. <i>Your registration is not valid until payment is received.</i>
                                            <br />
                                            <br />
                                            {conferenceAttendee.due !== null ? `Amount: $${conferenceAttendee.due}` : ""}
                                            <br />
                                            <br />
                                            Mail check to:
                                            <br />
                                            Jonathan Gittins
                                            <br />
                                            Delta College
                                            <br />
                                            1961 Delta Road
                                            <br />
                                            University Center, MI 48710
                                        </Text>
                                    </CardPreview>
                                </Card>
                                <br />
                            </>
                        )}
                        {conferenceAttendee.payby === "paypal" && (
                            <>
                                <Card>
                                    <CardHeader header={<h3>Payment by Paypal</h3>} />
                                    <CardPreview style={{ margin: 0, marginTop: -10 }}>
                                        <Text className={generalStyles.body2}>
                                            Click the button below to pay by paypal. Payment is verified manually, so please allow time for your confirmation to update.
                                            <br />
                                            <br />
                                            <i>Your registration is not valid until payment is received.</i>
                                            <br />
                                            <br />
                                            {conferenceAttendee.due !== null ? `Amount: $${conferenceAttendee.due}` : ""}
                                            <br />
                                            <br />
                                            <Button
                                                appearance="primary"
                                                onClick={() =>
                                                    window.open(
                                                        `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent("memberfee@2yc3.org")}&item_name=${encodeURIComponent(conferenceAttendee.institution)}_${encodeURIComponent(conferenceAttendee.first)}_${encodeURIComponent(
                                                            conferenceAttendee.last
                                                        )}&amount=${conferenceAttendee.due !== null ? conferenceAttendee.due : 50}`,
                                                        "_blank",
                                                        "rel=noopener noreferrer"
                                                    )
                                                }
                                            >
                                                Pay by Paypal
                                            </Button>
                                        </Text>
                                    </CardPreview>
                                </Card>
                                <br />
                            </>
                        )}

                        <Card>
                            <CardHeader style={{ margin: 0, marginTop: -10 }} header={<h3>Confirmation Code</h3>} />
                            <CardPreview style={{ margin: 0, marginTop: -10 }}>
                                <p>
                                    <Text size={800}>{searchParams.get("confirmationCode") || conferenceAttendee.confirmationCode}</Text>
                                </p>
                                <p>If you are not a member, keep this code safe in order to update your registration later. A copy of this code has been sent to the email you entered. Members can retrieve their registration via the account information button in the top right corner.</p>

                                <p>A summary of the information you entered is below. We look forward to seeing you at the conference!</p>
                            </CardPreview>
                        </Card>
                        <br />

                        <div></div>
                        <br />

                        {conference && conferenceAttendee && <RegistrationForm conference={conference} attendee={conferenceAttendee} readonly={true} />}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Confirmation;
